/**
 * In-memory state for the few things a demo user can change (sync buttons,
 * budget edits, referral follow-ups, notes). Lives for the browser tab;
 * a reload starts clean.
 */
import { iso, now } from './clock';

const store = new Map<string, unknown>();

export function getState<T>(key: string, init: () => T): T {
    if (!store.has(key)) {
        store.set(key, init());
    }
    return store.get(key) as T;
}

export function setState<T>(key: string, value: T): void {
    store.set(key, value);
}

/** When each data source was last "synced" — about an hour ago unless a sync was run. */
export function lastSyncedAt(type: string): string {
    return getState(`synced:${type}`, () => iso(new Date(now().getTime() - (40 + type.length * 7) * 60000)));
}

interface SyncRun {
    id: number;
    created: string;
    polls: number;
}

/** Called by every POST .../sync handler. */
export function queueSync(type: string, label: string): { status: string; message: string; last_synced_at: string } {
    const runs = getState<Record<string, SyncRun>>('syncRuns', () => ({}));
    runs[type] = { id: 900 + Object.keys(runs).length, created: iso(now()), polls: 0 };
    return { status: 'queued', message: `${label} sync has been queued. Data will update shortly.`, last_synced_at: lastSyncedAt(type) };
}

/** GET /api/sync/status/:type — in_progress on the first poll, completed after. */
export function syncStatus(type: string, board: boolean): Record<string, unknown> {
    const runs = getState<Record<string, SyncRun>>('syncRuns', () => ({}));
    const run = runs[type];
    const created = run?.created ?? lastSyncedAt(type);
    let status = 'completed';
    if (run) {
        run.polls++;
        status = run.polls < 2 ? 'in_progress' : 'completed';
        if (status === 'completed') {
            setState(`synced:${type}`, iso(now()));
        }
    }
    const out: Record<string, unknown> = {
        id: run?.id ?? 812,
        type,
        status,
        created_at: created,
        started_at: created,
        completed_at: status === 'completed' ? (run ? iso(now()) : created) : null,
    };
    if (!board) {
        out['error'] = null;
        out['warnings'] = [];
    }
    return out;
}
